// ── SpreadTotalSection — canonical SpreadsTotals EventRows in GameDetail
//
// Drops the value-dashboard's `EventRows` into GameDetail so the action
// rows + line picker + inline ladders behave identically across the
// SpreadsTotals page and this single-game surface. The MatchupRow is
// suppressed via `hideMatchupRow` — GameDetail's page header already
// shows the matchup + date, so a repeated "Away @ Home" row is noise.
//
// Section-local toolbar in the header controls marketSide + priceMode
// for this card only (independent from any dashboard-level toolbar).
// Defaults: Yes/No = NO, price mode = user's displaySettings default.
//
// Trade-click → forwards through to GameDetail's right-column trade
// ticket via onPillClick.

import { UsMetricHeaders } from '../ValueDashboard/us/UsMarketMetrics';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useWorkingView, useWorkingViewField } from '@/hooks/useWorkingView';
import { WorkingViewReset } from '@/components/atoms/WorkingViewReset';
import { readingInstance, spreadTotalLineReading, spreadTotalReading, type SpreadTotalLines } from './readingView';
import { useGameDetailResumesLatest } from './recentViews';
import { useAccountStore } from '../../stores/accountStore';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useMarketStore } from '../../stores/marketStore';
import { CELL_BOOK_VIEW_SUFFIX } from '../../stores/bookViewIds';
import { useLowBandwidth } from '../../stores/lowBandwidth';
import { useUserKalshiPOS } from '../../hooks/useUserKalshiPOS';
import { EdgeFadeScroller } from '@/components/cards/cardKit';
import {
  CompactLinesTable,
  GAME_DETAIL_TABLE_FADE,
  LineHoldStatus,
  closestKalshiYesTo50,
  spreadLineLabel,
  totalLineLabel,
  useLineListSettled,
  type LineHoldEntry,
} from './CompactLinesTable';
import { lineLadderReference } from './lineLadderReference';
import { usSpreadCell, usTotalCell } from '../SpreadsTotals/usLineCell';
import { GameRows } from '../ValueDashboard/GameTable';
import {
  LIQ_HEADER_TOOLTIP_KALSHI,
  LIQ_HEADER_TOOLTIP_POLY,
  VOL_HEADER_TOOLTIP_KALSHI,
  VOL_HEADER_TOOLTIP_POLY,
} from '../ValueDashboard/sharedBookCue';
import { Th } from '../../components/atoms/TableCells';
import { SectionToolbar } from './SectionToolbar';
import { buildSpreadBetRows, buildTotalBetRows } from './lineBetRows';
import { buildBetLineContext } from '../SpreadsTotals/buildBetLineContext';
import {
  linePriceCents,
  resolveLineIndexByKey,
  spreadLineKey,
  synthesizeFromSpread,
  synthesizeFromTotal,
  totalLineKey,
  venueAwareLineWindow,
} from '../SpreadsTotals/synthesize';
import type {
  GameEventRow,
  GameRow,
  OpBucketRef,
  SpreadLine,
  TeamSide,
  TotalLine,
  TradeTicketContext,
} from '../ValueDashboard/types';
import { lt2CardId, type PillTradeMeta } from '../SpreadsTotals/SpreadsTotalsTable';
import { tryOneClickFromTicketCtx } from '../ValueDashboard/oneClickIntercept';
import { useOneClickStore } from '../../stores/oneClickStore';
import { DISARM_REASON, disarmIfArmed } from '../ValueDashboard/oneClickDisarmReasons';
import { usGameKeyForBoardKey } from '@/lib/polymarketUs/gameMapping';
import { useLt2Access } from '../../hooks/useLt2Access';
import { useGrantPendingReason, useGrantSlot } from '../../hooks/useGrantSlot';
import { useLt2FocusStore } from '../../stores/lt2FocusStore';
import { usePinLineOnAim } from './usePinLineOnAim';
import { filterEventExecutableLines } from '../SpreadsTotals/synthesize';
import { INITIAL_LINE_WAIT_MS, initialLineVolumeEnabled } from '../SpreadsTotals/initialLineConfig';
import { initialLineDisplayIndex, settleInitialLine } from './initialLine';
import { elapsedSince, localClockStamp, type LocalClockStamp } from '@/lib/relayClock';
import { storedSpreadTotalLineIndex } from '../SpreadsTotals/storedLine';
import { spreadTotalLineScope } from '@/stores/spreadTotalLineMemory';
import { rememberSpreadTotalDefault } from '@/stores/spreadTotalLineDefaults';
import { recordInitialLine } from '@/stores/initialLineTally';
import { boardFeedOwnerRole } from '@/hooks/useBoardFeedNeeds';
import { useAuthStore } from '@/stores/authStore';
import { selectGrantsStatus } from '@/stores/grants';
import type { BetLineContext } from '../ValueDashboard/GameDetailPanel';

/** Bundle the parent uses to drive the right-side trade panel's
 *  spread/total mode — same shape SpreadsTotals on the value dashboard
 *  emits via onActiveGameChange + onActiveBetLineContextChange, just
 *  combined for the single consumer here. */
export interface ActiveSpreadTotal {
  game: GameRow;
  context: BetLineContext;
  /** The selected line's OddsPapi bucket, when the emitter knows it (Game
   *  Lines: period lines, ladders). This section is always full game, so it
   *  leaves it unset and GameDetail resolves the line itself. */
  opBucket?: OpBucketRef | null;
}

interface Props {
  game: GameRow;
  event: GameEventRow;
  /** Optional deep-link hint from the Positions page. If this ticker
   *  matches a spread or total line in the event, that row pre-expands
   *  with the matching line index pre-selected. */
  focusTicker?: string | null;
  /** #1238 — the page's reading focus (`gameDetailFocus`): `?ticker` or a US
   * focus. Absent: the ticker, as before. */
  readingFocus?: string | null;
  focusSide?: 'yes' | 'no';
  onPillClick: (ctx: TradeTicketContext) => void;
  /** Explicit inspection changes the viewed market without staging a price or executing. */
  onViewSelect?: (ctx: TradeTicketContext) => void;
  /** #1231 — a Polymarket US pill on the compact view: the page opens the US
   *  tab for that exact line (the line's own synthesized game). Its own path:
   *  never the one-click intercept. Absent → no US column. */
  onUsPillClick?: (ctx: TradeTicketContext) => void;
  /** Emits the active spread/total bundle (synth game + chip-strip
   *  context) so GameDetail's right-side panel can render its
   *  alternate header with the MiniLineStrip. Null when no row has
   *  been activated yet via a pill click. Fires on every line-index
   *  change too (panel chip selections come back through here). */
  onActiveLineChange?: (active: ActiveSpreadTotal | null, unavailable?: boolean) => void;
  /** Controlled activeKind from the parent. Lifted out of local state
   *  so a moneyline pill click at the parent level can force the
   *  section to stop being "active" — preventing the section's emit
   *  effect from clobbering the parent's null with a stale bundle on
   *  the next market-frame-driven event reference churn. */
  activeKind: 'spread' | 'total' | null;
  onActiveKindChange: (kind: 'spread' | 'total' | null) => void;
}

const VIEW_ID_PREFIX = 'game-detail-spread-total';

/** D-020 V2B — stable hotkey-focus scope; this section is always full-game. */
const LT2_GDST_CARD_SCOPE = {
  surface: 'gd-spread-total',
  period: 'full',
  keyPrefix: 'gdst',
} as const;

type SpreadTotalFocusMatch = {
  rowKind: 'spread' | 'total';
  idx: number;
  venue: 'kalshi' | 'polymarket';
};

function normalizeFocusTicker(ticker?: string | null): string | null {
  const trimmed = ticker?.trim();
  return trimmed ? trimmed.toUpperCase() : null;
}

export function findSpreadTotalFocusMatch(
  event: Pick<GameEventRow, 'spreads' | 'totals'>,
  focusTicker?: string | null,
): SpreadTotalFocusMatch | null {
  const target = normalizeFocusTicker(focusTicker);
  if (!target) return null;

  for (let i = 0; i < event.spreads.length; i++) {
    const s = event.spreads[i];
    const kalshi = normalizeFocusTicker(s.kalshi?.marketTicker);
    const poly = normalizeFocusTicker(s.polymarket?.marketTicker);
    if (kalshi === target) {
      return { rowKind: 'spread', idx: i, venue: 'kalshi' };
    }
    if (poly === target) {
      return { rowKind: 'spread', idx: i, venue: 'polymarket' };
    }
  }
  for (let i = 0; i < event.totals.length; i++) {
    const t = event.totals[i];
    const kalshi = normalizeFocusTicker(t.kalshi?.marketTicker);
    const poly = normalizeFocusTicker(t.polymarket?.marketTicker);
    if (kalshi === target) {
      return { rowKind: 'total', idx: i, venue: 'kalshi' };
    }
    if (poly === target) {
      return { rowKind: 'total', idx: i, venue: 'polymarket' };
    }
  }
  return null;
}

/** #1078 — the label a remembered line carries into Recent views and the
 * missing-pick note: the chip's own text (buildBetLineContext), led for a
 * spread by the away team whose line it is ("DAL +2.5"). A bare "+2.5" names
 * no team once it is read away from the strip. */
const spreadLabel = spreadLineLabel;
const totalLabel = totalLineLabel;

export function SpreadTotalSection(props: Props) {
  const [volumeRuleOn] = useState(initialLineVolumeEnabled);
  const lineScope = useAuthStore((s) => (volumeRuleOn ? spreadTotalLineScope(s.user) : null));
  // The new seeds and wait timer belong to one account/organization visit.
  // Auth refresh and cross-tab adoption can change that scope without the
  // route remounting. Keep the feature-off lifetime exactly as before; role
  // alone deliberately does not change the board's public line-choice scope.
  return <SpreadTotalSectionForScope key={lineScope ?? 'volume-rule-off'} {...props} lineScope={lineScope} />;
}

function SpreadTotalSectionForScope({
  game,
  event: eventProp,
  focusTicker,
  readingFocus,
  focusSide,
  onPillClick,
  onViewSelect,
  onUsPillClick,
  onActiveLineChange,
  activeKind,
  onActiveKindChange,
  lineScope,
}: Props & { lineScope: string | null }) {
  const defaultPriceMode = useDisplaySettings((s) => s.defaultPriceMode);
  // Odds filter (Dan 2026-08-23): U defaults OFF; thead + GameRows lockstep.
  const showUnbtdOdds = useDisplaySettings((s) => s.showUnbtdOdds);
  const hideOddsColumn = !showUnbtdOdds;
  const spreadTotalViewMode = useDisplaySettings((s) => s.spreadTotalViewMode);
  const setSpreadTotalViewMode = useDisplaySettings((s) => s.setSpreadTotalViewMode);
  const setOrderbookNeeds = useMarketStore((s) => s.setOrderbookNeeds);
  const openOrders = useAccountStore((s) => s.openOrders);

  // D-020 LT2-005: hide Poly-only lines from the execution view under lt2
  // (reveal toggle un-hides; revealed lines stay disabled). Filtered at the
  // top so every downstream read — defaults, focus match, subscriptions,
  // synth builders — sees one consistent world. lt2 off → prop untouched.
  const lt2Access = useLt2Access();
  const showPolyOnlyLines = useDisplaySettings((s) => s.spreadTotalShowPolyOnlyLines);
  const setShowPolyOnlyLines = useDisplaySettings((s) => s.setSpreadTotalShowPolyOnlyLines);
  const butterflySetting = useDisplaySettings((s) => s.spreadTotalNearestLines);
  const butterflyOn = lt2Access && butterflySetting && spreadTotalViewMode === 'compact';
  // #1070: while a restored lt2 grant confirms, the toggle and the filter
  // follow the grant SLOT, so neither the toggle popping in (U/OP slid 97px)
  // nor the hidden lines vanishing moves anything when access confirms. The
  // filter only hides lines the trader's own setting hides once confirmed,
  // and they are never tradable. If confirmation FAILS the slot stays pending
  // with the toggle disabled, so the lines are shown unfiltered rather than
  // left hidden with no way to reveal them.
  const lt2Slot = useGrantSlot('lt2Access');
  const lt2PendingReason = useGrantPendingReason();
  const lt2ConfirmFailed = useAuthStore((s) => selectGrantsStatus(s) === 'error');
  const butterflySlotOn = butterflySetting && spreadTotalViewMode === 'compact';
  const filterPolyOnly = lt2Access || (lt2Slot === 'pending' && !lt2ConfirmFailed);
  const hidePolyOnlyLines =
    filterPolyOnly && (!showPolyOnlyLines || (lt2Access ? butterflyOn : butterflySlotOn));
  const showPolyOnlyToggle = lt2Slot !== 'hidden' && !butterflySlotOn;
  const event = useMemo(
    () => (hidePolyOnlyLines ? filterEventExecutableLines(eventProp, false) : eventProp),
    [eventProp, hidePolyOnlyLines],
  );

  // Initial focus, when the line is already present on first render.
  // A follow-up effect below keeps retrying for cold hydration, where
  // the GameEventRow can exist before its target spread/total line does.
  const focusMatch = useMemo<SpreadTotalFocusMatch | null>(() => {
    return findSpreadTotalFocusMatch(event, focusTicker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resumeLatest = useGameDetailResumesLatest();
  const reading = useWorkingView(spreadTotalReading, readingInstance(event.key, readingFocus === undefined ? focusTicker : readingFocus, focusSide), {
    marketSide: focusSide ?? 'no', priceMode: defaultPriceMode,
  }, { resumeLatest });
  const [marketSide, setMarketSide] = useWorkingViewField(reading, 'marketSide');
  const [priceMode, setPriceMode] = useWorkingViewField(reading, 'priceMode');
  // #1071 — switched on (`initialLineConfig.ts`, read at mount like the
  // board), Game Detail opens on the line the board stored for this game: the
  // trader's board pick, else the board's seeded default. With neither, it
  // seeds its own by the board's rule and timing (`./initialLine.ts`): the 50¢
  // line shows unstored while the window's volume arrives, then one line is
  // kept for the visit. Switched off, or signed out, nothing is read, nothing
  // is timed, and the default below is today's.
  const volumeRuleActive = lineScope != null;
  // The line each strip settled on this visit: the board's line it fell back
  // to, or its own seed. Kept while listed, as the board keeps a row's key.
  const [seededLines, setSeededLines] = useState<{ spread: string | null; total: string | null }>(
    { spread: null, total: null });
  // When each strip began showing its 50¢ line while its window volume arrives.
  const initialLineWaitRef = useRef(new Map<'spread' | 'total', LocalClockStamp>());
  // Bumped when a wait runs out: settles again with no new frame.
  const [initialLineTick, setInitialLineTick] = useState(0);
  const boardSpreadIdx = useMemo(
    () => storedSpreadTotalLineIndex(lineScope, 'spread', event.key, event.spreads, spreadLineKey),
    [lineScope, event.key, event.spreads],
  );
  const boardTotalIdx = useMemo(
    () => storedSpreadTotalLineIndex(lineScope, 'total', event.key, event.totals, totalLineKey),
    [lineScope, event.key, event.totals],
  );
  // The line an untouched strip shows. Switched off: the closest-to-50¢ line,
  // re-picked as prices move. Switched on (#1071): the line it settled on
  // (the board's, or its own seed), else the board's line, else the rule's
  // answer (see above). Under LT2 the picked
  // market is sticky by identity across live reorder. Explicit non-LT2
  // picks also retain identity so the manual ticket cannot change strike. When we have a focusMatch, seed the corresponding
  // index from it instead.
  const defaultSpreadIdx = useMemo(
    () => volumeRuleActive
      ? initialLineDisplayIndex(event.spreads, spreadLineKey, boardSpreadIdx, seededLines.spread)
      : closestKalshiYesTo50(event.spreads.map(linePriceCents)),
    [volumeRuleActive, event.spreads, boardSpreadIdx, seededLines.spread],
  );
  const defaultTotalIdx = useMemo(
    () => volumeRuleActive
      ? initialLineDisplayIndex(event.totals, totalLineKey, boardTotalIdx, seededLines.total)
      : closestKalshiYesTo50(event.totals.map(linePriceCents)),
    [volumeRuleActive, event.totals, boardTotalIdx, seededLines.total],
  );
  const [spreadIdx, setSpreadIdx] = useState<number | null>(
    focusMatch?.rowKind === 'spread' ? focusMatch.idx : null,
  );
  const [totalIdx, setTotalIdx] = useState<number | null>(
    focusMatch?.rowKind === 'total' ? focusMatch.idx : null,
  );
  // #1078 — the picked line is part of this visit's reading view: a manual
  // entry starts on the default line, Back and a Recent views return bring
  // the pick back. A focus link's own match is its starting pick.
  const focusLines: SpreadTotalLines = useMemo(() => {
    const spread = focusMatch?.rowKind === 'spread' ? event.spreads[focusMatch.idx] : undefined;
    const total = focusMatch?.rowKind === 'total' ? event.totals[focusMatch.idx] : undefined;
    return {
      spread: spread ? { key: spreadLineKey(spread), label: spreadLabel(spread, event.awayCode) } : null,
      total: total ? { key: totalLineKey(total), label: totalLabel(total) } : null,
    };
    // Seeded once per visit, like focusMatch itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const lines = useWorkingView(spreadTotalLineReading,
    readingInstance(event.key, readingFocus === undefined ? focusTicker : readingFocus, focusSide), focusLines, { resumeLatest });
  const [pickedSpread, setPickedSpread] = useWorkingViewField(lines, 'spread');
  const [pickedTotal, setPickedTotal] = useWorkingViewField(lines, 'total');
  const spreadKey = pickedSpread?.key ?? null;
  const totalKey = pickedTotal?.key ?? null;
  const setSpreadKey = useCallback((key: string) => {
    const line = event.spreads.find((l) => spreadLineKey(l) === key);
    setPickedSpread({ key, label: line ? spreadLabel(line, event.awayCode) : '' });
  }, [event.spreads, event.awayCode, setPickedSpread]);
  const setTotalKey = useCallback((key: string) => {
    const line = event.totals.find((l) => totalLineKey(l) === key);
    setPickedTotal({ key, label: line ? totalLabel(line) : '' });
  }, [event.totals, setPickedTotal]);
  const [expandedRow, setExpandedRow] = useState<'spread' | 'total' | null>(
    focusMatch?.rowKind ?? null,
  );
  // Per-row expand state for the Detailed (GameRows) view — keyed by the synth
  // row's key + team side. Independent of the compact EventRows' 'spread'|'total'
  // expandedRow above, so switching views doesn't cross-drive an expansion.
  const [detailExpanded, setDetailExpanded] = useState<{ key: string; side: TeamSide } | null>(null);
  // #608 — see the twin in SpreadsTotals/index.tsx. The compact card keeps
  // its id while a vanished selection falls back to a visible default line,
  // so a held hotkey focus would otherwise point at a market the trader never
  // chose. Layout effect, and equality-guarded inside the store, so it can
  // never disturb another card's focus.
  useLayoutEffect(() => {
    const { clearFocusIf } = useLt2FocusStore.getState();
    if (spreadKey != null && !event.spreads.some((l) => spreadLineKey(l) === spreadKey)) {
      clearFocusIf(lt2CardId(LT2_GDST_CARD_SCOPE.keyPrefix, event.key, 'spread'), { marketGone: true });
    }
    if (totalKey != null && !event.totals.some((l) => totalLineKey(l) === totalKey)) {
      clearFocusIf(lt2CardId(LT2_GDST_CARD_SCOPE.keyPrefix, event.key, 'total'), { marketGone: true });
    }
  }, [event, spreadKey, totalKey]);

  // Explicit picks resolve by identity in both modes (#983). Retaining the
  // numeric index after a ladder insertion silently retargeted the manual
  // ticket while preserving its typed price and quantity.
  const spreadPickIdx = spreadKey == null ? -1 : event.spreads.findIndex((l) => spreadLineKey(l) === spreadKey);
  const totalPickIdx = totalKey == null ? -1 : event.totals.findIndex((l) => totalLineKey(l) === totalKey);
  const activeSpreadIdx = lt2Access
    ? resolveLineIndexByKey(event.spreads, spreadKey, spreadLineKey, defaultSpreadIdx)
    : (spreadPickIdx >= 0 ? spreadPickIdx : spreadKey == null ? (spreadIdx ?? defaultSpreadIdx) : defaultSpreadIdx);
  const activeTotalIdx = lt2Access
    ? resolveLineIndexByKey(event.totals, totalKey, totalLineKey, defaultTotalIdx)
    : (totalPickIdx >= 0 ? totalPickIdx : totalKey == null ? (totalIdx ?? defaultTotalIdx) : defaultTotalIdx);
  // A remembered or picked line that is no longer listed is said so: the
  // default line shows beside the note, never as a silent stand-in. Nothing is
  // said while the event has no lines at all. While lines are still arriving
  // the note says the pick is being looked for; "not listed" waits until the
  // line list has held still (#1087 review: a late line is not a gone line).
  const spreadMissing = pickedSpread != null && event.spreads.length > 0 && spreadPickIdx < 0;
  const totalMissing = pickedTotal != null && event.totals.length > 0 && totalPickIdx < 0;
  const spreadMissingSettled = useLineListSettled(
    spreadMissing, `${spreadKey}|${event.spreads.map(spreadLineKey).join(',')}`,
  );
  const totalMissingSettled = useLineListSettled(
    totalMissing, `${totalKey}|${event.totals.map(totalLineKey).join(',')}`,
  );
  // LT2-006 — aiming a card (row click, chip or badge) pins the line on
  // screen, so the moving closest-to-50¢ default cannot retarget A/D/S.
  const displayedSpread = event.spreads[activeSpreadIdx];
  const displayedTotal = event.totals[activeTotalIdx];
  // H-4 — a line the trader touched is HELD: the strip stays on it while
  // prices move or other strikes enter/leave the ladder.
  const spreadHeld = !spreadMissing && (spreadPickIdx >= 0 || (!lt2Access && spreadIdx != null));
  const totalHeld = !totalMissing && (totalPickIdx >= 0 || (!lt2Access && totalIdx != null));
  const lineStatus: LineHoldEntry[] = [];
  if (spreadMissing && pickedSpread) {
    lineStatus.push({ kind: 'spread', label: pickedSpread.label, state: spreadMissingSettled ? 'missing' : 'finding' });
  }
  if (totalMissing && pickedTotal) {
    lineStatus.push({ kind: 'total', label: pickedTotal.label, state: totalMissingSettled ? 'missing' : 'finding' });
  }
  if (spreadHeld && displayedSpread) {
    lineStatus.push({ kind: 'spread', label: spreadLabel(displayedSpread, event.awayCode), state: 'held' });
  }
  if (totalHeld && displayedTotal) {
    lineStatus.push({ kind: 'total', label: totalLabel(displayedTotal), state: 'held' });
  }
  /**
   * H-4 — touching the line on screen holds it. Expanding or inspecting a row,
   * or clicking its book, pins the DISPLAYED line's key when nothing is held
   * yet: the usePinLineOnAim mechanism, for every trader, hotkeys on or off.
   * Without it an expanded, armed book re-centred on another line with the
   * next tick, and a click there traded a line the trader never touched.
   * Never on mount: an untouched visit writes no pick into the reading view,
   * so Recent views and the missing-pick note only ever name a touched line.
   */
  const holdShownLine = (kind: 'spread' | 'total') => {
    if (kind === 'spread') {
      if (!spreadHeld && displayedSpread) setSpreadKey(spreadLineKey(displayedSpread));
    } else if (!totalHeld && displayedTotal) {
      setTotalKey(totalLineKey(displayedTotal));
    }
  };
  // A pick that is not listed pins nothing (#1078): aiming then pins the line
  // on screen. Left as the pin, the #608 clear above would drop every aim on
  // the next frame until a chip was picked.
  usePinLineOnAim({
    cardId: lt2Access ? lt2CardId(LT2_GDST_CARD_SCOPE.keyPrefix, event.key, 'spread') : null,
    pinnedKey: spreadPickIdx >= 0 ? spreadKey : null,
    displayedKey: displayedSpread ? spreadLineKey(displayedSpread) : null,
    pin: setSpreadKey,
  });
  usePinLineOnAim({
    cardId: lt2Access ? lt2CardId(LT2_GDST_CARD_SCOPE.keyPrefix, event.key, 'total') : null,
    pinnedKey: totalPickIdx >= 0 ? totalKey : null,
    displayedKey: displayedTotal ? totalLineKey(displayedTotal) : null,
    pin: setTotalKey,
  });
  // activeKind is parent-controlled — see Props above. Local alias keeps
  // the rest of the component readable.
  const setActiveKind = onActiveKindChange;
  const appliedFocusRef = useRef<string | null>(null);
  // #1071 — the line a focus link just applied, until a render shows it as the
  // pick. Under StrictMode the mount effect runs twice with the same render, so
  // the second run sees the focus applied but not yet its pick.
  const focusLineRef = useRef<{ kind: 'spread' | 'total'; key: string } | null>(null);
  useEffect(() => {
    const target = normalizeFocusTicker(focusTicker);
    // #1071 — switched on, settle each strip's initial line (see
    // `./initialLine.ts`): the board's line or the strip's own seed is kept for
    // the visit; a seed is also given to the board's default map and counted
    // like a board seed. Folded into this effect (it already sets state, the
    // #539 pattern), ahead of the focus below: a focus line about to be
    // applied in this pass counts as held, so the rule does not settle that
    // strip. Once applied it is an ordinary pick (held while listed).
    if (volumeRuleActive) {
      const focusPending = target != null && appliedFocusRef.current !== `${target}:${focusSide ?? ''}`;
      const applied = focusLineRef.current;
      if (applied && (applied.kind === 'spread' ? spreadKey : totalKey) === applied.key) focusLineRef.current = null;
      const focusKind = focusPending
        ? findSpreadTotalFocusMatch(event, target)?.rowKind ?? null
        : focusLineRef.current?.kind ?? null;
      const focusedCard = useLt2FocusStore.getState().focusedCardId;
      const settle = <T extends SpreadLine | TotalLine>(
        kind: 'spread' | 'total', lines: T[], keyFor: (line: T) => string,
        held: boolean, boardIdx: number | null,
      ) => {
        const waits = initialLineWaitRef.current;
        const started = waits.get(kind);
        const next = settleInitialLine({
          lines, keyFor, boardIdx, settledKey: seededLines[kind],
          held: held || focusKind === kind,
          worked: expandedRow === kind || activeKind === kind
            || focusedCard === lt2CardId(LT2_GDST_CARD_SCOPE.keyPrefix, event.key, kind),
          waitedMs: started ? elapsedSince(started) : null,
        });
        if (next.kind !== 'wait') waits.delete(kind);
        else if (!started) waits.set(kind, localClockStamp());
        if (next.kind === 'keep' || next.kind === 'seed') {
          setSeededLines((prev) => (prev[kind] === next.key ? prev : { ...prev, [kind]: next.key }));
        }
        if (next.kind === 'seed') {
          rememberSpreadTotalDefault(lineScope, kind, event.key, next.key);
          const role = next.outcome.kind === 'limit'
            ? boardFeedOwnerRole(event.sport, lines.some((line) => line.kalshi != null) ? 'kalshi' : 'polymarket') ?? 'unknown'
            : undefined;
          recordInitialLine(lineScope, kind, event.key, 'active', next.outcome, role);
        }
      };
      settle('spread', event.spreads, spreadLineKey, spreadPickIdx >= 0 || (!lt2Access && spreadIdx != null), boardSpreadIdx);
      settle('total', event.totals, totalLineKey, totalPickIdx >= 0 || (!lt2Access && totalIdx != null), boardTotalIdx);
    }
    if (!target) {
      appliedFocusRef.current = null;
      return;
    }
    const applyKey = `${target}:${focusSide ?? ''}`;
    if (appliedFocusRef.current === applyKey) return;
    const match = findSpreadTotalFocusMatch(event, target);
    if (!match) return;

    appliedFocusRef.current = applyKey;
    if (focusSide) setMarketSide(focusSide);
    if (match.rowKind === 'spread') {
      const line = event.spreads[match.idx];
      if (!line) return;
      setSpreadIdx(match.idx);
      setSpreadKey(spreadLineKey(line));
      focusLineRef.current = { kind: 'spread', key: spreadLineKey(line) };
      setExpandedRow(match.rowKind);
      setActiveKind(match.rowKind);
      onPillClick({
        game: synthesizeFromSpread(event, line),
        teamSide: 'away',
        venue: match.venue,
        initialOrderSide: focusSide ?? marketSide,
        postOnly: priceMode === 'maker',
      });
    } else {
      const line = event.totals[match.idx];
      if (!line) return;
      setTotalIdx(match.idx);
      setTotalKey(totalLineKey(line));
      focusLineRef.current = { kind: 'total', key: totalLineKey(line) };
      setExpandedRow(match.rowKind);
      setActiveKind(match.rowKind);
      onPillClick({
        game: synthesizeFromTotal(event, line),
        teamSide: 'away',
        venue: match.venue,
        initialOrderSide: focusSide ?? marketSide,
        postOnly: priceMode === 'maker',
      });
    }
  }, [event, focusTicker, focusSide, marketSide, onPillClick, priceMode, setActiveKind, setMarketSide, setSpreadKey, setTotalKey,
    volumeRuleActive, lineScope, seededLines, initialLineTick, expandedRow, activeKind, spreadKey, totalKey, spreadPickIdx, totalPickIdx, spreadIdx, totalIdx,
    lt2Access, boardSpreadIdx, boardTotalIdx]);

  // #1071 — wake the settle above when the earliest running wait runs out, so
  // a strip whose window volume never arrives is seeded with no further frame.
  // Switched off no wait is ever started, so no timer runs.
  useEffect(() => {
    if (!volumeRuleActive) return;
    let due = Infinity;
    for (const started of initialLineWaitRef.current.values()) {
      due = Math.min(due, INITIAL_LINE_WAIT_MS - elapsedSince(started));
    }
    if (due === Infinity) return;
    // Rounded up: a timer that fires a fraction early would find it waiting.
    const timer = setTimeout(() => setInitialLineTick((n) => n + 1), Math.max(0, Math.ceil(due)));
    return () => clearTimeout(timer);
  }, [volumeRuleActive, event, seededLines, initialLineTick, expandedRow, activeKind, spreadPickIdx, totalPickIdx]);

  // Books this section reads PER-DELTA: the selected line ±1 on each strip
  // (the chips the trader is about to pick, the inline ladder, the ticket)
  // plus, in the Detailed view, the expanded row's line. The compact strip
  // draws every other rung's PRICE from the row, not its book, so those are
  // no book need at all; the Detailed table draws K/P Liq cells for every
  // rung, which ride the coalesced `|cells` tier (marketStore
  // `isCellBookView`). Before 2026-10-09 every rung was per-delta: 99–133
  // books and 3.8–5.9k frames/s on one DAL@TB tab (bugfix 261008). A chip
  // beyond ±1 pays one subscribe round-trip on pick, as the board does.
  // Detailed-view rows: the same synth GameRows the Game Lines detailed table
  // builds from this period's spread/total lines (shared lineBetRows helper).
  const detailRows = useMemo(
    () => [...buildSpreadBetRows(event), ...buildTotalBetRows(event)],
    [event],
  );
  const addLineBooks = (set: Set<string>, l: SpreadLine | TotalLine | undefined) => {
    if (l?.kalshi?.marketTicker) set.add(l.kalshi.marketTicker);
    if (l?.polymarket?.marketTicker) set.add(l.polymarket.marketTicker);
  };
  // Low bandwidth mode: only the SELECTED line reads per-delta; its ±1
  // neighbours ride the coalesced `|cells` tier — still subscribed, so a pick
  // has a book at once and goes per-delta on the next render.
  const lowBandwidth = useLowBandwidth();
  const perDeltaKey = useMemo(() => {
    const set = new Set<string>();
    const lineWindow = (n: number, idx: number, all: number[]) => (lowBandwidth ? (n === 0 ? [] : [Math.min(Math.max(idx, 0), n - 1)]) : all);
    for (const i of lineWindow(event.spreads.length, activeSpreadIdx, venueAwareLineWindow(event.spreads, activeSpreadIdx))) addLineBooks(set, event.spreads[i]);
    for (const i of lineWindow(event.totals.length, activeTotalIdx, venueAwareLineWindow(event.totals, activeTotalIdx))) addLineBooks(set, event.totals[i]);
    if (spreadTotalViewMode === 'detailed' && detailExpanded) {
      // Detailed rows are this event's lines in order: spreads, then totals.
      const idx = detailRows.findIndex((r) => r.synth.key === detailExpanded.key);
      if (idx >= 0) addLineBooks(set, idx < event.spreads.length ? event.spreads[idx] : event.totals[idx - event.spreads.length]);
    }
    return [...set].sort().join('|');
  }, [event, activeSpreadIdx, activeTotalIdx, spreadTotalViewMode, detailExpanded, detailRows, lowBandwidth]);
  const cellsKey = useMemo(() => {
    const set = new Set<string>();
    if (spreadTotalViewMode === 'detailed') {
      for (const sp of event.spreads) addLineBooks(set, sp);
      for (const t of event.totals) addLineBooks(set, t);
    }
    if (lowBandwidth) {
      for (const i of venueAwareLineWindow(event.spreads, activeSpreadIdx)) addLineBooks(set, event.spreads[i]);
      for (const i of venueAwareLineWindow(event.totals, activeTotalIdx)) addLineBooks(set, event.totals[i]);
    }
    return [...set].sort().join('|');
  }, [event, spreadTotalViewMode, lowBandwidth, activeSpreadIdx, activeTotalIdx]);
  useEffect(() => {
    const viewId = `${VIEW_ID_PREFIX}:${event.key}`;
    setOrderbookNeeds(viewId, perDeltaKey === '' ? [] : perDeltaKey.split('|'));
    return () => {
      setOrderbookNeeds(viewId, []);
    };
  }, [event.key, perDeltaKey, setOrderbookNeeds]);
  useEffect(() => {
    const viewId = `${VIEW_ID_PREFIX}:${event.key}${CELL_BOOK_VIEW_SUFFIX}`;
    setOrderbookNeeds(viewId, cellsKey === '' ? [] : cellsKey.split('|'));
    return () => {
      setOrderbookNeeds(viewId, []);
    };
  }, [event.key, cellsKey, setOrderbookNeeds]);

  // Position/order slices for the same set of Kalshi tickers — drives
  // the strip's C/O rings via EventRows' stripAggregates calc.
  const kalshiTickers = useMemo<string[]>(() => {
    const out: string[] = [];
    for (const sp of event.spreads) if (sp.kalshi?.marketTicker) out.push(sp.kalshi.marketTicker);
    for (const t of event.totals) if (t.kalshi?.marketTicker) out.push(t.kalshi.marketTicker);
    return out;
  }, [event]);
  // #666: these sections render HUMAN order chips only, so they read the
  // display lane (bounded per-order retention with provenance) exactly like
  // the Value Dashboard's humanOrdersByTicker. Strict fail-closed consumers
  // (alerts/automation) live elsewhere and keep the strict lane.
  const { positionByTicker, ordersByTicker: strictOrdersByTicker, displayOrdersByTicker } =
    useUserKalshiPOS(kalshiTickers);
  const ordersByTicker = displayOrdersByTicker ?? strictOrdersByTicker;


  const inspectLine = (kind: 'spread' | 'total', side = marketSide, index?: number, venue?: 'kalshi' | 'polymarket') => {
    const idx = index ?? (kind === 'spread' ? activeSpreadIdx : activeTotalIdx);
    const line = kind === 'spread' ? event.spreads[idx] : event.totals[idx];
    if (!line) return;
    if (index == null) holdShownLine(kind);
    else if (kind === 'spread') { setSpreadIdx(idx); setSpreadKey(spreadLineKey(line as SpreadLine)); }
    else { setTotalIdx(idx); setTotalKey(totalLineKey(line as TotalLine)); }
    setActiveKind(kind);
    setMarketSide(side);
    setExpandedRow(kind);
    onViewSelect?.({
      game: kind === 'spread'
        ? synthesizeFromSpread(event, line as SpreadLine)
        : synthesizeFromTotal(event, line as TotalLine),
      teamSide: 'away', venue: venue ?? (line.kalshi ? 'kalshi' : 'polymarket'),
      initialOrderSide: side, postOnly: priceMode === 'maker',
    });
  };

  const handlePillTrade = (
    rowKind: 'spread' | 'total',
    venue: 'kalshi' | 'polymarket',
    side: 'yes' | 'no',
    cents: number,
    meta?: PillTradeMeta,
  ) => {
    setActiveKind(rowKind);
    // A click on the row's own book holds the line it was showing (H-4).
    if (meta?.lineIdx == null) holdShownLine(rowKind);
    // A multi-line block click names its OWN line via meta.lineIdx; follow
    // the click so the panel/chips agree with the market just traded.
    const clickedIdx =
      meta?.lineIdx ?? (rowKind === 'spread' ? activeSpreadIdx : activeTotalIdx);
    if (meta?.lineIdx != null) {
      if (rowKind === 'spread') {
        setSpreadIdx(meta.lineIdx);
        const line = event.spreads[meta.lineIdx];
        if (line) setSpreadKey(spreadLineKey(line));
      } else {
        setTotalIdx(meta.lineIdx);
        const line = event.totals[meta.lineIdx];
        if (line) setTotalKey(totalLineKey(line));
      }
    }
    // D-020 interception, at the section level because the ticket ctx below
    // deliberately carries the PARENT game (panel resolves the bet through
    // onActiveLineChange) — the intercept needs the clicked LINE's own synth
    // so identity + guard run against the actual spread/total book.
    const activeLine =
      rowKind === 'spread' ? event.spreads[clickedIdx] : event.totals[clickedIdx];
    if (activeLine) {
      const synth =
        rowKind === 'spread'
          ? synthesizeFromSpread(event, activeLine as SpreadLine)
          : synthesizeFromTotal(event, activeLine as TotalLine);
      if (
        tryOneClickFromTicketCtx({
          ctx: {
            game: synth,
            teamSide: 'away',
            venue,
            prefillPrice: cents,
            initialOrderSide: side,
            postOnly: priceMode === 'maker',
            meta,
          },
          surface: 'gd-spread-total',
          period: 'full',
          priceMode,
        })
      ) {
        return;
      }
    }
    // Leaving the hot path on a genuine priced click disarms (V1 boundary).
    if (useOneClickStore.getState().armed) useOneClickStore.getState().disarm('This market or venue uses the order ticket. Re-arm to return to supported one-click markets.');
    onPillClick({
      game,
      meta,
      teamSide: 'away',
      venue,
      initialOrderSide: side,
      prefillPrice: cents,
      postOnly: priceMode === 'maker',
    });
    // Flip the section's YES/NO toggle to match the pill — same UX
    // rule the moneyline section uses.
    if (side !== marketSide) setMarketSide(side);
  };

  // #1231 — a US pill: the same line selection as a K/P click, then the US
  // path with the clicked line's own synthesized game (so the US tab joins
  // that exact line). Disarms like the moneyline US pill; never intercepted.
  const handleUsPillTrade = (rowKind: 'spread' | 'total', side: 'yes' | 'no', cents: number) => {
    if (!onUsPillClick) return;
    setActiveKind(rowKind);
    holdShownLine(rowKind);
    const line = rowKind === 'spread' ? event.spreads[activeSpreadIdx] : event.totals[activeTotalIdx];
    if (!line) return;
    disarmIfArmed(DISARM_REASON.usPrice);
    onUsPillClick({
      game: rowKind === 'spread' ? synthesizeFromSpread(event, line as SpreadLine) : synthesizeFromTotal(event, line as TotalLine),
      teamSide: 'away',
      venue: 'polymarket',
      prefillPrice: cents,
      initialOrderSide: side,
      postOnly: false,
    });
    if (side !== marketSide) setMarketSide(side);
  };
  const showUsColumn = !!onUsPillClick && usGameKeyForBoardKey(event.key) !== null;
  const usKey = usGameKeyForBoardKey(event.key);
  const usCandidates = showUsColumn && usKey ? useMarketStore.getState().getUsLineCandidates(usKey) : { spreads: [], totals: [] };

  // Stable callbacks so buildBetLineContext's closures don't churn
  // every render. Keep these outside the effect for the same reason.
  const handlePanelSpreadSelect = useCallback((next: number) => {
    setSpreadIdx(next);
    const line = event.spreads[next];
    if (line) setSpreadKey(spreadLineKey(line));
  }, [event.spreads, setSpreadKey]);
  const handlePanelTotalSelect = useCallback((next: number) => {
    setTotalIdx(next);
    const line = event.totals[next];
    if (line) setTotalKey(totalLineKey(line));
  }, [event.totals, setTotalKey]);

  // Emit the active spread/total bundle whenever the kind or its idx
  // changes. Covers two paths:
  //   • Pill click in this section → setActiveKind → effect fires →
  //     parent learns this section is now the active source.
  //   • Chip click in the panel's MiniLineStrip → onSelectIdx
  //     (wired below) → setSpreadIdx/setTotalIdx → effect fires →
  //     parent + panel resync to the new line.
  //
  // INVARIANT: at most ONE emit per (kind, idx, event-frame). The bundle is
  // freshly allocated each emit, and the parent stores it with setState — so
  // a render-driven re-fire (e.g. an unstable callback prop identity, the
  // exact bug a0453ef introduced and 3c6bca9 fixed) would otherwise loop
  // render→emit→setState→render unboundedly and wedge the main thread.
  // The ref guard makes that whole failure class structurally impossible
  // (no React Compiler here — React 18, manual memoization only) while
  // preserving the per-frame re-emits the order panel needs to tick prices
  // (`event` re-derives with a new identity on every market frame).
  const lastEmitRef = useRef<{
    kind: 'spread' | 'total';
    idx: number;
    event: typeof event;
  } | null>(null);
  useEffect(() => {
    if (!onActiveLineChange) return;
    if (activeKind == null) {
      lastEmitRef.current = null;
      onActiveLineChange(null);
      return;
    }
    // A missing pick may show a fallback beside the existing line-status
    // notice, but that fallback is not a new executable selection. Only an
    // explicit chip/book action can choose a different ticket target.
    const pickMissing = activeKind === 'spread'
      ? spreadKey != null && spreadPickIdx < 0
      : totalKey != null && totalPickIdx < 0;
    if (pickMissing) {
      lastEmitRef.current = null;
      onActiveLineChange(null, true);
      return;
    }
    const activeIdx = activeKind === 'spread' ? activeSpreadIdx : activeTotalIdx;
    const last = lastEmitRef.current;
    if (last && last.kind === activeKind && last.idx === activeIdx && last.event === event) {
      return; // same selection, same frame — nothing new to tell the parent
    }
    lastEmitRef.current = { kind: activeKind, idx: activeIdx, event };
    if (activeKind === 'spread') {
      const line = event.spreads[activeSpreadIdx];
      if (!line) {
        lastEmitRef.current = null;
        onActiveLineChange(null, true);
        return;
      }
      onActiveLineChange({
        game: synthesizeFromSpread(event, line),
        context: buildBetLineContext({
          kind: 'spread',
          event,
          lines: event.spreads,
          selectedIdx: activeSpreadIdx,
          onSelectIdx: handlePanelSpreadSelect,
        }),
      });
    } else {
      const line = event.totals[activeTotalIdx];
      if (!line) {
        lastEmitRef.current = null;
        onActiveLineChange(null, true);
        return;
      }
      onActiveLineChange({
        game: synthesizeFromTotal(event, line),
        context: buildBetLineContext({
          kind: 'total',
          event,
          lines: event.totals,
          selectedIdx: activeTotalIdx,
          onSelectIdx: handlePanelTotalSelect,
        }),
      });
    }
  }, [
    activeKind,
    activeSpreadIdx,
    activeTotalIdx,
    spreadKey,
    totalKey,
    spreadPickIdx,
    totalPickIdx,
    event,
    onActiveLineChange,
    handlePanelSpreadSelect,
    handlePanelTotalSelect,
  ]);

  return (
    <section data-view-anchor="spread-total" className="bg-card border-border rounded-lg border">
      <div className="border-border flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <h2 className="text-foreground text-lg font-semibold">Spread / Total</h2>
        <LineHoldStatus entries={lineStatus} untouched={volumeRuleActive ? 'settles' : 'follows'} />
        {/* Same trailing cluster as Game Lines: toolbar, hairline, Reset view. */}
        <div className="flex shrink-0 items-center gap-2">
          <SectionToolbar
            priceMode={priceMode}
            onPriceMode={setPriceMode}
            marketSide={marketSide}
            onMarketSide={setMarketSide}
            viewMode={spreadTotalViewMode}
            onViewMode={setSpreadTotalViewMode}
            // The chip reads what is on screen: a failed confirmation shows
            // every line, so it reads "on" whatever the saved setting is.
            showPolyOnlyLines={showPolyOnlyToggle ? showPolyOnlyLines || !hidePolyOnlyLines : undefined}
            onShowPolyOnlyLines={showPolyOnlyToggle ? setShowPolyOnlyLines : undefined}
            polyOnlyPendingReason={lt2Slot === 'pending' ? lt2PendingReason : undefined}
          />
          <span aria-hidden="true" className="bg-border/60 h-4 w-px" />
          <WorkingViewReset
            label="Spread and Total"
            scope="price mode and YES/NO side"
            reset={reading.reset}
            undo={reading.undo}
            canUndo={reading.canUndo}
          />
        </div>
      </div>
      {spreadTotalViewMode === 'detailed' ? (
        <EdgeFadeScroller className={GAME_DETAIL_TABLE_FADE}>
          <table className="w-full border-collapse border-spacing-0">
            <thead className="bg-muted/50 border-border border-b">
              <tr>
                <Th className="w-60">Bet</Th>
                {!hideOddsColumn ? (
                  <Th className="w-16 text-center" title="Unabated consensus odds (full-game only) — also hosts your resting orders in the ladder">UNBTD</Th>
                ) : (
                  <Th className="w-10 text-center" title="Your resting orders at each ladder price level">{''}</Th>
                )}
                <Th className="w-32 text-center">Kalshi</Th>
                <Th className="w-16 text-right" title={VOL_HEADER_TOOLTIP_KALSHI}>K Vol</Th>
                <Th className="w-24 text-right" title={LIQ_HEADER_TOOLTIP_KALSHI}>K Liq</Th>
                <Th className="w-16 text-right" title={`Best-bid depth on the active ${marketSide.toUpperCase()} side`}>K Q</Th>
                <Th className="w-16 text-right" title="Kalshi best-bid depth change over the last 1m">K Δ/m</Th>
                <Th className="w-32 text-center">Poly</Th>
                <Th className="w-16 text-right" title={VOL_HEADER_TOOLTIP_POLY}>P Vol</Th>
                <Th className="w-24 text-right" title={LIQ_HEADER_TOOLTIP_POLY}>P Liq</Th>
                <Th className="w-16 text-right" title="Polymarket best-bid depth change over the last 1m">P Δ/m</Th>
                {showUsColumn && <><Th className="w-32 text-center">US</Th><UsMetricHeaders /></>}
              </tr>
            </thead>
            <tbody>
              {detailRows.map((r, i) => (
                <GameRows
                  key={r.synth.key}
                  game={r.synth}
                  showUsColumn={showUsColumn}
                  usLine={i < event.spreads.length
                    ? usSpreadCell(event, event.spreads[i], usCandidates.spreads)
                    : usTotalCell(event, event.totals[i - event.spreads.length], usCandidates.totals)}
                  onUsTradeClick={(_key, side, opts) => {
                    const kind = i < event.spreads.length ? 'spread' : 'total';
                    const idx = kind === 'spread' ? i : i - event.spreads.length;
                    if (kind === 'spread') { setSpreadIdx(idx); setSpreadKey(spreadLineKey(event.spreads[idx])); }
                    else { setTotalIdx(idx); setTotalKey(totalLineKey(event.totals[idx])); }
                    setActiveKind(kind);
                    if (opts?.initialOrderSide) setMarketSide(opts.initialOrderSide);
                    disarmIfArmed(DISARM_REASON.usPrice);
                    onUsPillClick?.({ game: r.synth, teamSide: side, venue: 'polymarket', postOnly: false,
                      initialOrderSide: opts?.initialOrderSide, prefillPrice: opts?.priceCents });
                  }}
                  gameIndex={i}
                  marketSide={marketSide}
                  priceMode={priceMode}
                  expandedTeamSide={detailExpanded?.key === r.synth.key ? detailExpanded.side : null}
                  ladderLevels={5}
                  dualOutcomeLadder
                  lineReference={lineLadderReference(event, i < event.spreads.length ? 'spread' : 'total',
                    i < event.spreads.length ? event.spreads[i] : event.totals[i - event.spreads.length])}
                  onSummaryYesSelect={(key, side, venue) => {
                    setDetailExpanded({ key, side });
                    inspectLine(i < event.spreads.length ? 'spread' : 'total', 'yes',
                      i < event.spreads.length ? i : i - event.spreads.length, venue);
                  }}
                  onSummaryNoSelect={(key, side, venue) => {
                    setDetailExpanded({ key, side });
                    inspectLine(i < event.spreads.length ? 'spread' : 'total', 'no',
                      i < event.spreads.length ? i : i - event.spreads.length, venue);
                  }}
                  onToggleExpand={(key, side) => {
                    const closing = detailExpanded?.key === key && detailExpanded.side === side;
                    setDetailExpanded(closing ? null : { key, side });
                    if (!closing) inspectLine(i < event.spreads.length ? 'spread' : 'total', marketSide,
                      i < event.spreads.length ? i : i - event.spreads.length);
                  }}
                  onTradeClick={(_key, side, venue, opts) => {
                    // Detailed rows ARE this period's spread/total lines: rows
                    // [0..spreads.length) are spreads, the rest totals. Select
                    // the matching line so the section's emit effect re-drives
                    // the right-side panel with the correct synth — the parent's
                    // livePanelGame prioritizes activeSpreadTotal.game (from
                    // onActiveLineChange) over the click-time tradeCtx, so
                    // without this a detailed click could open the ticket on a
                    // stale line left active by a prior compact selection.
                    const kind: 'spread' | 'total' = i < event.spreads.length ? 'spread' : 'total';
                    const idx = kind === 'spread' ? i : i - event.spreads.length;
                    if (kind === 'spread') {
                      setSpreadIdx(idx);
                      const line = event.spreads[idx];
                      if (line) setSpreadKey(spreadLineKey(line));
                    } else {
                      setTotalIdx(idx);
                      const line = event.totals[idx];
                      if (line) setTotalKey(totalLineKey(line));
                    }
                    setActiveKind(kind);
                    if (opts?.initialOrderSide) setMarketSide(opts.initialOrderSide);
                    const ctx: TradeTicketContext = {
                      game: r.synth,
                      teamSide: side,
                      venue,
                      prefillPrice: opts?.priceCents,
                      initialOrderSide: opts?.initialOrderSide,
                      // GameRows' ladder derives postOnly itself (bid = join);
                      // no meta here, so the intercept honors this value.
                      postOnly: opts?.postOnly ?? false,
                    };
                    // D-020 interception — the synth ctx here already names
                    // the exact line market, so it feeds the intercept as-is.
                    if (
                      tryOneClickFromTicketCtx({
                        ctx,
                        surface: 'gd-spread-total',
                        period: 'full',
                        priceMode,
                      })
                    ) {
                      return;
                    }
                    if (useOneClickStore.getState().armed && opts?.priceCents != null) {
                      useOneClickStore.getState().disarm('This market or venue uses the order ticket. Re-arm to return to supported one-click markets.');
                    }
                    onPillClick(ctx);
                  }}
                  positionByTicker={positionByTicker}
                  ordersByTicker={ordersByTicker}
                  hideDateColumn
                  hideScoreColumn
                  hideOddsColumn={hideOddsColumn}
                  singleSide={r.singleSide}
                  sideTint
                  labelWidthPx={200}
                />
              ))}
            </tbody>
          </table>
        </EdgeFadeScroller>
      ) : (
        <CompactLinesTable
          onInspectSide={(kind, side, venue) => inspectLine(kind, side, undefined, venue)}
          event={event}
          marketSide={marketSide}
          priceMode={priceMode}
          positionByTicker={positionByTicker}
          ordersByTicker={ordersByTicker}
          openOrders={openOrders}
          spreadIdx={activeSpreadIdx}
          totalIdx={activeTotalIdx}
          expandedRow={expandedRow}
          onSelectSpreadIdx={(idx) => {
            // Chip-strip scrolls implicitly activate the spread row,
            // matching the value-dashboard behavior: trader touching a chip
            // on the spread row but the panel is showing the total context
            // was confusing. Activate + expand on chip touch so the side
            // panel + emitted bundle both follow the chip.
            setSpreadIdx(idx);
            const line = event.spreads[idx];
            if (line) setSpreadKey(spreadLineKey(line));
            setActiveKind('spread');
            setExpandedRow('spread');
            inspectLine('spread', marketSide, idx);
          }}
          onSelectTotalIdx={(idx) => {
            setTotalIdx(idx);
            const line = event.totals[idx];
            if (line) setTotalKey(totalLineKey(line));
            setActiveKind('total');
            setExpandedRow('total');
            inspectLine('total', marketSide, idx);
          }}
          onToggleExpand={(rowKind) => {
            if (expandedRow !== rowKind) inspectLine(rowKind);
            else setExpandedRow(null);
          }}
          onPillTrade={handlePillTrade}
          showUsColumn={showUsColumn}
          onUsPillTrade={handleUsPillTrade}
          lt2CardScope={LT2_GDST_CARD_SCOPE}
        />
      )}
    </section>
  );
}
